import React, { useContext, useEffect } from 'react'
import Participants from './Participants.js';
import IecAction from './IecAction.js';
import IecInfo from './IecInfo.js';
import EditableItemList from './EditableItemList';
import { AgendaContext } from '../context/AgendaContext';
import styled from 'styled-components';
import IecClcWgReports from './IecClcWgReports.js';
import ClcAction from './ClcAction';
import ClcInfo from './ClcInfo';
import UpComingIntlMeetings from './UpComingIntlMeetings.js';
import ProposalForSweTitles from './ProposalForSweTitles.js';
import ToggleSection from './ToggleSection';
import { Color, Colors } from '../styles/Colors';
import UpComingSweMeetings from './UpComingSweMeetings.js';
import { FontScale } from '../styles/FontScale';
import RecentlyDeterminedPublications from './RecentlyDeterminedPublications.js';
import SwicthButton from './SwicthButton';
import { Trans, useTranslation } from 'react-i18next';

const Wrapper = styled.div`
    & > h2 {
        padding: 1.5rem 0 1.5rem 0; 
    }
`;

const ToggleTocWrapper = styled.div`
    display: flex; 
    flex-direction: row; 
    align-items: center; 
    padding: 0 0 1rem 0; 

    & > h3 {
        padding: 0 0 0 1rem; 

        & > span {
            font-style: italic; 
            font-size: ${FontScale.size_3}; 
        }
    }

    & > input[type="checkbox"] {
        width: 20px;
        height: 20px;

        &:checked::after {
            font-size: ${FontScale.size_1};
            line-height: 18px; 
        }
    }
`;

/* Create a list of internal links*/ 
const TableOfContents = styled.ol`
    & > li {
        margin: 0 0 0.5rem 0;
        
        &:last-of-type {
            margin: 0;
        }

        a {
            color: ${Color(Colors.black)}; 
        } 
    }
`;

const Program = () => {
    const {
        previousProtocol, setPreviousProtocol, iecActionDocs, setIecActionDocs, plenaryScheduleImage, setPlenaryScheduleImage, correspondenceDocs, 
        setCorrespondenceDocs, toggleToc, setToggleToc, editableSections, setEditableSections
    } = useContext(AgendaContext);
    const { t } = useTranslation(); 

    useEffect(() => {
        localStorage.setItem('previous_protocol', previousProtocol)
    }, [previousProtocol])

    const handleToggleToc = (event) => {
        const checked = event.target.checked;
        localStorage.setItem('toggle_table_of_contents', JSON.stringify(checked)); 
        setToggleToc(checked);
    }; 

    /* Lowercase first character and append rest of the string
       Example: Dagordning > d + agordning > dagordning  */

    const firstLetterToLowercase = (text) => 
        text.charAt(0).toLowerCase() + text.slice(1); 


    return (
        <Wrapper>
            <h2>{t('tableOfContets.headTitle')}</h2>
            <ToggleTocWrapper>
                <SwicthButton 
                    checked={toggleToc}
                    onChange={handleToggleToc}
                    size='medium'
                />
                <h3>
                    {toggleToc ? `${t('common.addBtnLabel')} ` : `${t('common.removeBtnLabel')} `}
                    <Trans 
                        i18nKey='tableOfContets.excludeInDocument'
                        components={{
                            italic: <i key="italic" /> 
                        }}
                    />
                </h3>
            </ToggleTocWrapper>
            {toggleToc && (
                <ToggleSection 
                    title={t('tableOfContets.sectionLabel')}
                    toc='toc'
                >
                    <TableOfContents>
                        <li><a href='#approval-of-the-previous-minutes'>{t('tableOfContets.items.approvalOfThePreviousMinutes')}</a></li>
                        <li><a href='#participants'>{t('tableOfContets.items.participants')}</a></li>
                        <li><a href='#decisions-by-written-procedure'>{t('tableOfContets.items.consensusByCorrespondence')}</a></li>
                        <li><a href='#iec-documents-for-action'>{t('tableOfContets.items.iecDocumentsForAction')}</a></li>
                        <li><a href='#clc-documents-for-action'>{t('tableOfContets.items.clcDocumentsForAction')}</a></li>
                        <li><a href='#upcoming-swedish-meetings'>{t('tableOfContets.items.upcomingSwedishMeetings')}</a></li>
                        <li><a href='#upcoming-international-meetings'>{t('tableOfContets.items.upcomingInternationalMeetings')}</a></li>
                        <li><a href='#discussion-proposed-swedish-titles'>{t('tableOfContets.items.proposalForSwedishTitles')}</a></li>
                        <li><a href='#recently-determined-publications'>{t('tableOfContets.items.recentlyDeterminedPublications')}</a></li>
                        <li><a href='#reports-from-iec-&-clc-working-groups'>{t('tableOfContets.items.reportsFromIecAndClcWorkingGroups')}</a></li>
                        <li><a href='#iec-documents-for-info'>{t('tableOfContets.items.iecDocumentsForInfo')}</a></li>
                        <li><a href='#clc-documents-for-info'>{t('tableOfContets.items.clcDocumentsForInfo')}</a></li>
                        <li><a href='#other-info-&-activities'>{t('tableOfContets.items.otherInfoAndActivities')}</a></li>
                        <li><a href='#any-other-business'>{t('tableOfContets.items.anyOtherBusiness')}</a></li>
                        <li><a href='#recurring-topics'>{t('tableOfContets.items.recurringTopics')}</a></li>
                    </TableOfContents>
                </ToggleSection>
            )}
            <div>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.approvalOfThePreviousMinutes'))}
                    moduleId='approval-of-the-previous-minutes'
                >
                    <input
                        type='text'
                        value={previousProtocol || ''}
                        onChange={(event) => setPreviousProtocol(event.target.value || '')}
                        placeholder={t('approvalOfThePreviousMinutes.label')}
                        spellCheck={false}
                    />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.participants'))}
                    moduleId='participants'
                >
                    <Participants />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.consensusByCorrespondence'))}
                    moduleId='decisions-by-written-procedure'
                >
                    <IecAction
                        docs={correspondenceDocs}
                        setDocs={setCorrespondenceDocs}
                        type='correspondencen_docs'
                        addItemMsg={`${t('common.addBtnLabel')} ${firstLetterToLowercase(t('tableOfContets.items.consensusByCorrespondence'))}`}
                        editItemMsg={`${t('common.edit')} ${firstLetterToLowercase(t('tableOfContets.items.consensusByCorrespondence'))}`}
                    />
                </ToggleSection>
                <ToggleSection
                    title={t('tableOfContets.items.iecDocumentsForAction')}
                    moduleId='iec-documents-for-action'
                >
                    <IecAction
                        docs={iecActionDocs}
                        setDocs={setIecActionDocs}
                        plenaryScheduleImage={plenaryScheduleImage}
                        setPlenaryScheduleImage={setPlenaryScheduleImage}
                        type='action_docs'
                        addItemMsg={`${t('common.addBtnLabel')} ${t('actionDocs.iecDocument')}`}
                        editItemMsg={`${t('common.edit')} ${t('actionDocs.iecDocument')}`}

                    />
                </ToggleSection>
                <ToggleSection
                    title={t('tableOfContets.items.clcDocumentsForAction')}
                    moduleId='clc-documents-for-action'
                >
                    <ClcAction />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.upcomingSwedishMeetings'))}
                    moduleId='upcoming-swedish-meetings'
                >
                    <UpComingSweMeetings />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.upcomingInternationalMeetings'))}
                    moduleId='upcoming-international-meetings'
                >
                    <UpComingIntlMeetings />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.proposalForSwedishTitles'))}
                    moduleId='discussion-proposed-swedish-titles'
                >
                    <ProposalForSweTitles />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.recentlyDeterminedPublications'))}
                    moduleId='recently-determined-publications'
                >
                    <RecentlyDeterminedPublications />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.reportsFromIecAndClcWorkingGroups'))}
                    moduleId='reports-from-iec-&-clc-working-groups'
                >
                    <IecClcWgReports />
                </ToggleSection>
                <ToggleSection
                    title={t('tableOfContets.items.iecDocumentsForInfo')}
                    moduleId='iec-documents-for-info'
                >
                    <IecInfo />
                </ToggleSection>
                <ToggleSection
                    title={t('tableOfContets.items.clcDocumentsForInfo')}
                    moduleId='clc-documents-for-info'
                >
                    <ClcInfo />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.otherInfoAndActivities'))}
                    moduleId='other-info-&-activities'
                >
                    <EditableItemList
                        addItems={editableSections.otherInfoAndActivities}
                        setAddItems={(value) => 
                            setEditableSections(prev => ({
                                ...prev, 
                                otherInfoAndActivities: 
                                    typeof value === 'function'
                                        ? value(prev.otherInfoAndActivities)
                                        : value
                            }))
                        }
                        storageKey='other_info_and_activities'
                        emptyListMsg={t('common.noContent')}
                        addItemMsg={`${t('common.addBtnLabel')} ${t('otherInfoAndActivities')}`}
                        editItemMsg={`${t('common.edit')} ${t('otherInfoAndActivities')}`}
                        buttonColorTheme='darkBlue'
                    />
                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.anyOtherBusiness'))}
                    moduleId='any-other-business'
                >
                    <EditableItemList
                        addItems={editableSections.anyOtherBusiness}
                        setAddItems={(value) => 
                            setEditableSections(prev => ({
                                ...prev, 
                                anyOtherBusiness: 
                                    typeof value === 'function'
                                        ? value(prev.anyOtherBusiness)
                                        : value
                            }))
                        }
                        storageKey='any_other_business'
                        emptyListMsg={t('common.noContent')}
                        addItemMsg={`${t('common.addBtnLabel')} ${t('anyOtherBusiness')}`}
                        editItemMsg={`${t('common.edit')} ${t('anyOtherBusiness')}`}
                        buttonColorTheme='darkBlue'
                    />

                </ToggleSection>
                <ToggleSection
                    title={firstLetterToLowercase(t('tableOfContets.items.recurringTopics'))}
                    moduleId='recurring-topics'
                >
                    <EditableItemList
                        addItems={editableSections.recurringTopics}
                        setAddItems={(value) => 
                            setEditableSections(prev => ({
                                ...prev, 
                                recurringTopics: 
                                    typeof value === 'function'
                                        ? value(prev.recurringTopics)
                                        : value
                            }))
                        }
                        storageKey='recurring_topics'
                        emptyListMsg={t('common.noContent')}
                        addItemMsg={`${t('common.addBtnLabel')} ${t('recurringTopics')}`}
                        editItemMsg={`${t('common.edit')} ${t('recurringTopics')}`}
                        buttonColorTheme='darkBlue'
                    />

                </ToggleSection>
            </div>
        </Wrapper>
    )
}

export default Program